import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";

/** Auth0 can throw this when silent token renewal needs user consent (e.g. first time with audience). */
function isConsentRequiredError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { error?: string; message?: string };
  return (
    e.error === "consent_required" ||
    (typeof e.message === "string" && e.message.toLowerCase().includes("consent required"))
  );
}

/**
 * Custom hook for authentication with token management.
 * Use getAccessToken() for API calls: it tries silent auth first, then prompts with a popup if consent is required.
 */
export function useAuth() {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<Error | null>(null);

  /** Get token; on "Consent required" falls back to popup so the user can grant consent. */
  const getAccessToken = useCallback(async (): Promise<string> => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      if (isConsentRequiredError(error)) {
        const token = await getAccessTokenWithPopup();
        if (token === undefined) throw new Error("No access token returned");
        return token;
      }
      throw error;
    }
  }, [getAccessTokenSilently, getAccessTokenWithPopup]);

  // Fetch access token when authenticated (uses same fallback so token state stays in sync)
  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) {
        setAccessToken(null);
        return;
      }

      try {
        const token = await getAccessToken();
        setAccessToken(token);
        setTokenError(null);
      } catch (error) {
        setTokenError(error as Error);
        setAccessToken(null);
      }
    };

    fetchToken();
  }, [isAuthenticated, getAccessToken]);

  // Login handler
  const login = useCallback(() => {
    loginWithRedirect({
      appState: { returnTo: "/dashboard" },
    });
  }, [loginWithRedirect]);

  // Logout handler
  const handleLogout = useCallback(() => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [logout]);

  return {
    isAuthenticated,
    isLoading,
    user,
    accessToken,
    tokenError,
    login,
    logout: handleLogout,
    /** Prefer this over getAccessTokenSilently: handles "Consent required" via popup. */
    getAccessToken,
    getAccessTokenSilently,
  };
}

